import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, Star, Loader2, Pencil } from 'lucide-react';
import { PhotoTagEntityType, isPlannerOrAbove } from '@peles/shared';
import { useAuth } from '../context/AuthContext';
import { useVendor } from '../hooks/useVendors';
import { ExpenseListView } from '../components/expenses/ExpenseListView';
import { PhotoListView } from '../components/photos/PhotoListView';
import { EntityModal, PageSectionsLayout } from '../components/common';
import type { SectionDef } from '../components/common';
import { RecordConversations } from '../components/messaging/RecordConversations';
import {
  VendorDetailPanel,
  VendorBalanceSummary,
  VendorEventsTile,
  VendorLogo,
} from '../components/vendors';
import { VendorContactList } from '../components/vendors/VendorContactList';
import { VendorDecorPieces } from '../components/decor-library/VendorDecorPieces';
import { VendorCategoryChips } from '../components/vendors/VendorCategoryChips';
import { CommissionListView } from '../components/commissions/CommissionListView';
import { VendorPaymentMethods } from '../components/vendors/payment-methods/VendorPaymentMethods';
import { ContactMoreMenu } from '../components/common/ContactMoreMenu';
import { PhoneLink } from '../components/common/PhoneLink';

export function VendorDetailPage() {
  const { vendorId } = useParams<{ vendorId: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [showEdit, setShowEdit] = useState(false);
  const { data: vendor, isLoading } = useVendor(vendorId || '');

  const isPlanner = currentUser ? isPlannerOrAbove(currentUser.systemRole) : false;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={32} className="animate-spin text-primary-600" />
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="text-center py-20">
        <p className="text-gray-500">Vendor not found</p>
        <button
          onClick={() => navigate('/vendors')}
          className="mt-4 text-primary-600 hover:text-primary-700 text-sm"
        >
          Back to Vendors
        </button>
      </div>
    );
  }

  const sections: SectionDef[] = [
    {
      id: 'conversations',
      title: 'Conversations',
      content: <RecordConversations entityType="Vendor" entityId={vendorId!} />,
    },
    {
      id: 'payment-methods',
      title: 'Payment Methods',
      content: (
        <VendorPaymentMethods
          vendorId={vendor.id}
          methods={vendor.paymentMethods ?? []}
          canManage={isPlanner}
        />
      ),
    },
    {
      id: 'contacts',
      title: 'Contacts',
      content: <VendorContactList vendorId={vendorId!} />,
    },
    ...(isPlanner
      ? [
          {
            id: 'decor',
            title: 'Decor they supply',
            content: <VendorDecorPieces vendorId={vendorId!} />,
          },
        ]
      : []),
    {
      id: 'commissions',
      title: 'Commissions',
      content: <CommissionListView vendorId={vendorId} />,
    },
    {
      id: 'expenses',
      title: 'Expenses',
      content: (
        <ExpenseListView
          entityType="VENDOR"
          entityId={vendorId}
          showEventColumn
          currentUser={currentUser ?? undefined}
        />
      ),
    },
    {
      id: 'photos',
      title: 'Photos',
      content: (
        <PhotoListView
          entityType={PhotoTagEntityType.VENDOR}
          entityId={vendorId}
          limit={12}
          enableUpload
          currentUser={currentUser || undefined}
        />
      ),
    },
    ...(vendor.notes
      ? [
          {
            id: 'notes',
            title: 'Notes',
            content: <p className="text-sm text-gray-600 whitespace-pre-wrap">{vendor.notes}</p>,
          },
        ]
      : []),
  ];

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-3"
        >
          <ArrowLeft size={16} />
          Back
        </button>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-start gap-3">
            <VendorLogo vendorId={vendor.id} logoUrl={vendor.logoUrl} editable={isPlanner} />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{vendor.businessName}</h1>
              <div className="flex items-center gap-4 mt-2 text-sm text-gray-500 flex-wrap">
                {vendor.contactName && (
                  <span className="text-gray-700 font-medium">{vendor.contactName}</span>
                )}
                {vendor.city && <span>{vendor.city}</span>}
                {vendor.phone && (
                  <PhoneLink
                    phone={vendor.phone}
                    name={vendor.businessName}
                    icon={<Phone size={14} />}
                  />
                )}
                {vendor.rating != null && (
                  <span className="flex items-center gap-0.5 text-amber-500">
                    <Star size={14} className="fill-amber-500" />
                    {vendor.rating}
                  </span>
                )}
                {!vendor.isActive && (
                  <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                    Inactive
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setShowEdit(true)}
              className="flex items-center gap-1.5 px-3 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Pencil size={16} />
              Edit
            </button>
            <ContactMoreMenu email={vendor.email} website={vendor.website} />
          </div>
        </div>
      </div>

      {/* Categories */}
      <div className="mb-6">
        <VendorCategoryChips
          vendorId={vendor.id}
          categories={vendor.categories}
          canManage={isPlanner}
        />
      </div>

      {/* The events it is on — the office's view of the events list (a vendor
          reading its own page cannot list events). */}
      {isPlanner && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
          <VendorEventsTile vendorId={vendorId!} />
        </div>
      )}

      {/* Balance summary */}
      <VendorBalanceSummary vendorId={vendorId!} />

      {/* Sections */}
      <PageSectionsLayout
        pageKey="vendor-detail"
        userId={currentUser?.id ?? 'guest'}
        sections={sections}
      />

      {/* Edit Modal */}
      {currentUser && (
        <EntityModal
          open={showEdit}
          onClose={() => setShowEdit(false)}
          title={`Edit: ${vendor.businessName}`}
          size="lg"
        >
          <VendorDetailPanel
            vendorId={vendorId!}
            onClose={() => setShowEdit(false)}
            onUnsavedChange={() => {}}
            currentUser={currentUser}
          />
        </EntityModal>
      )}
    </div>
  );
}
